import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { PALLET_TYPES, DEFAULT_PALLET_TYPE } from '../../../constants/pallets';
import { ProductGroupListing } from './productGroups';

export type PanelEditTarget =
  | { kind: 'product'; groupKey: string }
  | { kind: 'sku'; groupKey: string; inventorySkuId: string }
  | {
      kind: 'pallet';
      groupKey: string;
      inventorySkuId: string;
      palletIndex: number;
    };

export type DimensionsDraft = {
  length: string;
  width: string;
  height: string;
  unitOfMeasure: string;
};

export type ProductPanelDraft = {
  productId: string;
  name: string;
  barcode: string;
  dimensions: DimensionsDraft;
  weight: string;
  weightUnitOfMeasure: string;
  volume: string;
  volumeUnitOfMeasure: string;
  temperatureControlType: string;
  description: string;
  supplier: string;
  brand: string;
  category: string;
  subCategory: string;
  chosenImageFile: File | null;
};

export type AdditionalFieldDraft = { name: string; value: string };

export type SkuPanelDraft = {
  skuId: string;
  name: string;
  barcode: string;
  unitOfMeasure: string;
  amountOfProductInSku: string;
  dimensions: DimensionsDraft;
  grossWeight: string;
  grossWeightUnitOfMeasure: string;
  grossVolume: string;
  grossVolumeUnitOfMeasure: string;
  description: string;
  minimumOrderQuantity: string;
  coPackingSupplier: string;
  additionalFields: AdditionalFieldDraft[];
  chosenImageFile: File | null;
};

export type PalletPanelDraft = {
  type: string;
  customPalletSpecification: string;
  dimensions: DimensionsDraft;
  weight: string;
  weightUnitOfMeasure: string;
  skusPerLayer: string;
  layers: string;
  isNew: boolean;
};

export const DIMENSION_UNIT_OPTIONS = ['cm', 'm'];
export const PALLET_DIMENSION_UNIT_OPTIONS = ['mm', 'cm', 'm'];
export const WEIGHT_UNIT_OPTIONS = ['g', 'Kg', 'oz', 'lb'];
export const PALLET_WEIGHT_UNIT_OPTIONS = ['Kg', 'lb'];
export const VOLUME_UNIT_OPTIONS = ['ml', 'l', 'cm3', 'dm3', 'm3'];
export const TEMPERATURE_CONTROL_OPTIONS = ['Ambient', 'Chilled', 'Frozen'];
export const PALLET_TYPE_OPTIONS = PALLET_TYPES.map((type) => type.value);
export const SKU_TYPE_OPTIONS = ['Item', 'Case', 'Pallet', 'Other'];
export const DEFAULT_SKU_TYPE = 'Case';

const cubicUnitLabels: Record<string, string> = {
  mm3: 'mm³',
  cm3: 'cm³',
  dm3: 'dm³',
  m3: 'm³',
};

export const unitLabel = (unit: string): string =>
  cubicUnitLabels[unit] ?? unit;

export const optionsIncludingStoredValue = (
  value: string,
  options: string[]
): string[] => (options.includes(value) ? options : [value, ...options]);

const asText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

const dimensionsDraftOf = (
  dimensions:
    | {
        length?: number | null;
        width?: number | null;
        height?: number | null;
        dimensionsUnitOfMeasure?: string | null;
      }
    | null
    | undefined,
  fallbackUnit: string
): DimensionsDraft => ({
  length: asText(dimensions?.length),
  width: asText(dimensions?.width),
  height: asText(dimensions?.height),
  unitOfMeasure: dimensions?.dimensionsUnitOfMeasure || fallbackUnit,
});

export const productDraftOf = (
  group: ProductGroupListing
): ProductPanelDraft => {
  const { product } = group;
  const representative = group.skus[0];
  return {
    productId: asText(product?.productId),
    name: asText(product?.name),
    barcode: asText(product?.barcode?.barcode),
    dimensions: dimensionsDraftOf(product?.dimensions, 'cm'),
    weight: asText(product?.weight),
    weightUnitOfMeasure: product?.weightUnitOfMeasure || 'g',
    volume: asText(product?.netVolume),
    volumeUnitOfMeasure: product?.netVolumeUnitOfMeasure || 'cm3',
    temperatureControlType: asText(
      representative?.temperatureControlRequirements?.temperatureControlType
    ),
    description: asText(product?.description),
    supplier: asText(product?.supplier),
    brand: asText(product?.brand),
    category: asText(product?.category),
    subCategory: asText(product?.subCategory),
    chosenImageFile: null,
  };
};

export const skuDraftOf = (sku: InventorySKUListing): SkuPanelDraft => ({
  skuId: asText(sku.skuId),
  name: asText(sku.name),
  barcode: asText(sku.barcode?.barcode),
  unitOfMeasure: asText(sku.unitOfMeasure) || DEFAULT_SKU_TYPE,
  amountOfProductInSku: asText(sku.amountOfProductInSku),
  dimensions: dimensionsDraftOf(sku.dimensions, 'cm'),
  grossWeight: asText(sku.grossWeight),
  grossWeightUnitOfMeasure: sku.grossWeightUnitOfMeasure || 'Kg',
  grossVolume: asText(sku.grossVolume),
  grossVolumeUnitOfMeasure: sku.grossVolumeUnitOfMeasure || 'l',
  description: asText(sku.description),
  minimumOrderQuantity: asText(sku.minimumOrderQuantity),
  coPackingSupplier: asText(sku.coPackingSupplier),
  additionalFields: (sku.metaData ?? []).map((field) => ({
    name: asText(field.name),
    value: asText(field.value),
  })),
  chosenImageFile: null,
});

export const palletDraftOf = (
  pallet: InventorySKUListing['palletising'][number]
): PalletPanelDraft => ({
  type: pallet.type || DEFAULT_PALLET_TYPE,
  customPalletSpecification: asText(pallet.customPalletSpecification),
  dimensions: dimensionsDraftOf(pallet.dimensions, 'cm'),
  weight: asText(pallet.palletWeight),
  weightUnitOfMeasure: pallet.palletWeightUnitOfMeasure || 'Kg',
  skusPerLayer: asText(pallet.amountOfSkusInLayer),
  layers: asText(pallet.amountOfLayersInPallet),
  isNew: false,
});

export const emptyPalletDraft = (): PalletPanelDraft => ({
  type: DEFAULT_PALLET_TYPE,
  customPalletSpecification: '',
  dimensions: { length: '', width: '', height: '', unitOfMeasure: 'cm' },
  weight: '',
  weightUnitOfMeasure: 'Kg',
  skusPerLayer: '',
  layers: '',
  isNew: true,
});

const cubicMetreDivisors: Record<string, number> = {
  mm: 1_000_000_000,
  cm: 1_000_000,
  dm: 1_000,
  m: 1,
};

export const draftPalletVolume = (draft: PalletPanelDraft): string => {
  const length = Number(draft.dimensions.length);
  const width = Number(draft.dimensions.width);
  const height = Number(draft.dimensions.height);
  const divisor = cubicMetreDivisors[draft.dimensions.unitOfMeasure];
  const hasEveryDimension =
    draft.dimensions.length !== '' &&
    draft.dimensions.width !== '' &&
    draft.dimensions.height !== '';
  if (
    !hasEveryDimension ||
    !divisor ||
    !Number.isFinite(length) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height)
  )
    return '—';
  return `${((length * width * height) / divisor).toFixed(2)} m³`;
};

export const draftSkusPerPallet = (draft: PalletPanelDraft): string => {
  const perLayer = Number(draft.skusPerLayer);
  const layers = Number(draft.layers);
  if (
    draft.skusPerLayer === '' ||
    draft.layers === '' ||
    !Number.isFinite(perLayer) ||
    !Number.isFinite(layers)
  )
    return '—';
  return (perLayer * layers).toLocaleString();
};

export const isSkuDraftSaveable = (draft: SkuPanelDraft): boolean =>
  draft.skuId.trim().length > 0;

export const isPalletDraftSaveable = (draft: PalletPanelDraft): boolean =>
  !draft.isNew ||
  (draft.skusPerLayer.trim().length > 0 && draft.layers.trim().length > 0);

export type ActivePanelEdit =
  | { kind: 'product'; groupKey: string; draft: ProductPanelDraft }
  | {
      kind: 'sku';
      groupKey: string;
      inventorySkuId: string;
      draft: SkuPanelDraft;
    }
  | {
      kind: 'pallet';
      groupKey: string;
      inventorySkuId: string;
      palletIndex: number;
      draft: PalletPanelDraft;
    };

export const isActiveEditSaveable = (edit: ActivePanelEdit): boolean => {
  if (edit.kind === 'sku') return isSkuDraftSaveable(edit.draft);
  if (edit.kind === 'pallet') return isPalletDraftSaveable(edit.draft);
  return true;
};

export const withAnEmptyAdditionalField = (
  draft: SkuPanelDraft
): SkuPanelDraft => ({
  ...draft,
  additionalFields: [...draft.additionalFields, { name: '', value: '' }],
});

export const withoutBlankAdditionalFields = (
  fields: AdditionalFieldDraft[]
): AdditionalFieldDraft[] =>
  fields.filter((field) => field.name.trim().length > 0);
